import {
  ArrowThinDownIcon,
  Button,
  ErrorDialog,
  Skeleton,
  Typography,
} from "@codat/orchard-ui";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { useCreateThreadMessage } from "hooks/query/useCreateThreadMessage";
import { useGetThreadMessages } from "hooks/query/useGetThreadMessages";
import { useIsScrolledToBottom } from "hooks/useIsScrolledToBottom";
import React, { useEffect, useRef, useState } from "react";

import styles from "./AIChat.module.scss";
import { Message, PromptBox, WelcomeScreen } from "./components";

interface AIChatProps {
  companyId: string;
  threadId: string;
}

const AIChat = ({ companyId, threadId }: AIChatProps) => {
  const [promptValue, setPromptValue] = useState("");
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const mostRecentMessageRef = useRef<HTMLDivElement | null>(null);
  const { isAtBottom, bottomMarkerRef } = useIsScrolledToBottom(messagesRef);
  const {
    data: messages,
    isLoading,
    isFetching,
    error,
    isError,
  } = useGetThreadMessages({
    companyId,
    threadId,
  });

  const transitionStyles = useSpring({
    opacity: isAtBottom || isLoading || !messages?.length ? 0 : 1,
    config: { duration: 150 },
  });

  const {
    startStreaming: startStreamingReply,
    isStreaming: isStreamingReply,
    displayedText: replyText,
  } = useCreateThreadMessage({
    companyId,
    threadId,
  });

  const scrollToBottom = ({
    behavior = "smooth",
  }: Pick<ScrollToOptions, "behavior"> = {}) => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior,
      });
    }
  };

  const onPromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isStreaming || !promptValue.trim()) return;
    startStreamingReply({
      prompt: promptValue.trim(),
    });
    setPromptValue("");
  };

  const isStreaming = isStreamingReply;
  const displayedText = replyText;
  const noMessagesExist = !isLoading && !messages?.length && !isError;

  useEffect(() => {
    if (isStreamingReply && mostRecentMessageRef.current) {
      mostRecentMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isStreamingReply]);

  useEffect(() => {
    if (!isLoading) {
      scrollToBottom({ behavior: "auto" });
    }
  }, [isLoading]);

  if (isError) {
    const threadNotFound = error.response?.status === 404;
    return (
      <section className={styles.errorPage}>
        <ErrorDialog
          statusCode={threadNotFound ? 404 : undefined}
          message={threadNotFound ? "Conversation not found" : undefined}
          hideContactSupportButton
          hideRefreshPageButton
        />
      </section>
    );
  }

  return (
    <section
      className={classNames(styles.chat, {
        [styles.isWelcome]: noMessagesExist && !isStreaming,
      })}
      data-testid="ai-chat"
    >
      {noMessagesExist && !isStreaming && (
        <WelcomeScreen
          onPromptClick={(promptText) => {
            startStreamingReply({
              prompt: promptText.trim(),
            });
          }}
        />
      )}
      {isLoading && !isStreaming && <Skeleton className={styles.skeleton} />}

      <div className={styles.messagesWrapper}>
        {!!messages?.length && (
          <div className={styles.messages} ref={messagesRef}>
            {messages.map((m, i) => (
              <Message
                key={i}
                role={m.role}
                content={m.textContent}
                createdAt={m.createdAt}
                id={m.id}
                companyId={companyId}
                threadId={threadId}
                feedbackScore={m.feedbackScore}
                mostRecentMessageRef={
                  i === messages.length - 1 ? mostRecentMessageRef : null
                }
              />
            ))}
            {(isFetching || isStreaming) && !isLoading && (
              <Message
                createdAt={new Date().toISOString()}
                content={displayedText}
                role="assistant"
                id=""
                companyId={companyId}
                threadId={threadId}
                isThinking={isStreaming && !displayedText}
                isStreaming={isStreaming}
              />
            )}
            <div ref={bottomMarkerRef} className={styles.bottomMarker}></div>
          </div>
        )}
        <animated.div
          className={styles.scrollToBottom}
          style={transitionStyles}
        >
          <Button
            className={styles.scrollBtn}
            label="Scroll to bottom"
            onClick={() => scrollToBottom()}
            color="interface"
            variant="secondary"
          >
            <ArrowThinDownIcon />
          </Button>
        </animated.div>
      </div>
      <form
        className={styles.promptBoxWrapper}
        onSubmit={onPromptSubmit}
        ref={formRef}
      >
        <PromptBox
          onChange={(e) => setPromptValue(e.target.value)}
          value={promptValue}
          formRef={formRef}
          isLoading={isStreaming}
        />
      </form>
      <Typography
        as="small"
        variant="body_sm"
        className={styles.disclaimer}
        data-testid="disclaimer"
      >
        Summary based on supplier analysis reports from the current company only
        and limited to the data provided. This feature is in alpha.
      </Typography>
    </section>
  );
};

export default AIChat;
